import { useEffect, useState } from 'react';
import { api } from '../App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card } from '@/components/ui/card';
import { Plus, CheckCircle, Circle, Clock } from '@phosphor-icons/react';
import ClientAutocomplete from '../components/ClientAutocomplete';

export default function VATPage() {
  const [vatRecords, setVatRecords] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);

  const currentMonth = new Date().toLocaleString('sr-Latn', { month: 'long' });
  const currentYear = new Date().getFullYear();

  const [formData, setFormData] = useState({
    client_id: '',
    mjesec: currentMonth,
    godina: currentYear,
    dokumentacija_dostavljena: false,
    obracun_zavrsen: false,
    prijava_podnijeta: false,
    iznos_tip: 'nula',
    iznos_pdv: 0,
  });

  useEffect(() => {
    fetchVatRecords();
    fetchClients();
  }, []);

  const fetchVatRecords = async () => {
    try {
      const response = await api.get('/vat');
      setVatRecords(response.data);
    } catch (error) {
      toast.error('Greška pri učitavanju PDV zapisa');
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await api.get('/clients');
      setClients(response.data);
    } catch (error) {
      toast.error('Greška pri učitavanju klijenata');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingRecord) {
        await api.put(`/vat/${editingRecord.id}`, formData);
        toast.success('PDV zapis ažuriran');
      } else {
        await api.post('/vat', formData);
        toast.success('PDV zapis kreiran');
      }
      setIsDialogOpen(false);
      resetForm();
      fetchVatRecords();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Greška pri čuvanju PDV zapisa');
    }
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    setFormData({ ...record });
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      client_id: '',
      mjesec: currentMonth,
      godina: currentYear,
      dokumentacija_dostavljena: false,
      obracun_zavrsen: false,
      prijava_podnijeta: false,
      iznos_tip: 'nula',
      iznos_pdv: 0,
    });
    setEditingRecord(null);
  };

  const getStatusColor = (record) => {
    if (record.prijava_podnijeta) return 'bg-emerald-50 text-emerald-700 border-emerald-200';
    if (record.obracun_zavrsen) return 'bg-amber-50 text-amber-700 border-amber-200';
    return 'bg-red-50 text-red-700 border-red-200';
  };

  const getStatusIcon = (record) => {
    if (record.prijava_podnijeta) return <CheckCircle size={20} weight="duotone" className="text-emerald-700" />;
    if (record.obracun_zavrsen) return <Clock size={20} weight="duotone" className="text-amber-700" />;
    return <Circle size={20} weight="duotone" className="text-red-700" />;
  };

  const getStatusText = (record) => {
    if (record.prijava_podnijeta) return 'Završeno';
    if (record.obracun_zavrsen) return 'U toku';
    return 'Hitno';
  };

  return (
    <div className="space-y-6" data-testid="vat-page">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-medium text-slate-900">PDV Praćenje - {currentMonth} {currentYear}</h3>
          <p className="text-sm text-slate-600 mt-1">Pregled PDV obaveza za tekući mjesec</p>
        </div>

        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button
              onClick={resetForm}
              className="bg-blue-600 hover:bg-blue-700 text-white rounded-md px-4 py-2 font-medium"
              data-testid="vat-add-button"
            >
              <Plus size={20} weight="bold" className="mr-2" />
              Dodaj PDV zapis
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg" data-testid="vat-dialog">
            <DialogHeader>
              <DialogTitle className="text-xl font-medium text-slate-900">
                {editingRecord ? 'Uredi PDV zapis' : 'Dodaj PDV zapis'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4" data-testid="vat-form">
              <div className="space-y-2">
                <Label className="text-xs font-semibold tracking-wider uppercase text-slate-500">Klijent *</Label>
                <ClientAutocomplete clients={clients} value={formData.client_id} onChange={(id) => setFormData({ ...formData, client_id: id })} testid="vat-client-select" placeholder="Pretraga po nazivu ili PIB-u..." />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs font-semibold tracking-wider uppercase text-slate-500">Mjesec *</Label>
                  <Input
                    required
                    value={formData.mjesec}
                    onChange={(e) => setFormData({ ...formData, mjesec: e.target.value })}
                    className="bg-white border-slate-300 text-sm"
                    data-testid="vat-mjesec-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-semibold tracking-wider uppercase text-slate-500">Godina *</Label>
                  <Input
                    required
                    type="number"
                    value={formData.godina}
                    onChange={(e) => setFormData({ ...formData, godina: parseInt(e.target.value) })}
                    className="bg-white border-slate-300 text-sm"
                    data-testid="vat-godina-input"
                  />
                </div>
              </div>

              <div className="space-y-3">
                <Label className="text-xs font-semibold tracking-wider uppercase text-slate-500">Status</Label>
                <div className="space-y-2">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.dokumentacija_dostavljena}
                      onChange={(e) => setFormData({ ...formData, dokumentacija_dostavljena: e.target.checked })}
                      className="rounded border-slate-300"
                      data-testid="vat-dokumentacija-checkbox"
                    />
                    <span className="text-sm text-slate-700">Dokumentacija dostavljena</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.obracun_zavrsen}
                      onChange={(e) => setFormData({ ...formData, obracun_zavrsen: e.target.checked })}
                      className="rounded border-slate-300"
                      data-testid="vat-obracun-checkbox"
                    />
                    <span className="text-sm text-slate-700">Obračun završen</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.prijava_podnijeta}
                      onChange={(e) => setFormData({ ...formData, prijava_podnijeta: e.target.checked })}
                      className="rounded border-slate-300"
                      data-testid="vat-prijava-checkbox"
                    />
                    <span className="text-sm text-slate-700">Prijava podnijeta</span>
                  </label>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-xs font-semibold tracking-wider uppercase text-slate-500">Tip iznosa</Label>
                <Select
                  value={formData.iznos_tip}
                  onValueChange={(value) => setFormData({ ...formData, iznos_tip: value })}
                >
                  <SelectTrigger className="bg-white border-slate-300" data-testid="vat-iznos-tip-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="uplata">Za uplatu</SelectItem>
                    <SelectItem value="kredit">Kredit</SelectItem>
                    <SelectItem value="nula">Nula</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-xs font-semibold tracking-wider uppercase text-slate-500">Iznos PDV (€)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.iznos_pdv}
                  onChange={(e) => setFormData({ ...formData, iznos_pdv: parseFloat(e.target.value) })}
                  className="bg-white border-slate-300 text-sm"
                  data-testid="vat-iznos-input"
                />
              </div>

              <div className="flex gap-3 justify-end pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsDialogOpen(false)}
                  className="rounded-md px-4 py-2 font-medium"
                  data-testid="vat-cancel-button"
                >
                  Odustani
                </Button>
                <Button
                  type="submit"
                  className="bg-blue-600 hover:bg-blue-700 text-white rounded-md px-4 py-2 font-medium"
                  data-testid="vat-submit-button"
                >
                  {editingRecord ? 'Ažuriraj' : 'Kreiraj'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <p className="text-slate-500">Učitavanje...</p>
        </div>
      ) : vatRecords.length === 0 ? (
        <Card className="p-8 text-center" data-testid="vat-empty-state">
          <p className="text-slate-500">Nema PDV zapisa</p>
        </Card>
      ) : (
        <div className="grid gap-4" data-testid="vat-records-grid">
          {vatRecords.map((record) => {
            const client = clients.find((c) => c.id === record.client_id);
            return (
              <Card
                key={record.id}
                className={`p-5 border rounded-md ${getStatusColor(record)}`}
                data-testid={`vat-record-${record.id}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    {getStatusIcon(record)}
                    <div>
                      <h4 className="font-medium text-slate-900" data-testid={`vat-client-${record.id}`}>
                        {client?.skraceni_naziv || client?.naziv_firme || 'Nepoznat klijent'}
                      </h4>
                      <p className="text-sm text-slate-600 mt-1">
                        {record.mjesec} {record.godina}
                      </p>
                      <div className="mt-3 flex gap-4 text-xs">
                        <span className={record.dokumentacija_dostavljena ? 'text-emerald-700' : 'text-slate-500'}>
                          {record.dokumentacija_dostavljena ? '✓' : '✗'} Dokumentacija
                        </span>
                        <span className={record.obracun_zavrsen ? 'text-emerald-700' : 'text-slate-500'}>
                          {record.obracun_zavrsen ? '✓' : '✗'} Obračun
                        </span>
                        <span className={record.prijava_podnijeta ? 'text-emerald-700' : 'text-slate-500'}>
                          {record.prijava_podnijeta ? '✓' : '✗'} Prijava
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-xs font-semibold tracking-wider uppercase text-slate-500">
                      {getStatusText(record)}
                    </p>
                    <p className="text-lg font-semibold text-slate-900 mt-1" data-testid={`vat-iznos-${record.id}`}>
                      {record.iznos_pdv.toFixed(2)} €
                    </p>
                    <p className="text-xs text-slate-600 mt-1">{record.iznos_tip}</p>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEdit(record)}
                      className="mt-3"
                      data-testid={`vat-edit-${record.id}`}
                    >
                      Uredi
                    </Button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
